import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { planRenameSchema } from '@app/shared';
import type { PlanDto } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { TextInput, applyServerErrors } from '../../components/form';
import { useRenamePlan } from './api';

interface Props {
  /** The plan to rename; the dialog is open while this is set. */
  plan: PlanDto | null;
  onClose: () => void;
}

/** "Edit Plan Name" action of the plan list. */
export function PlanNameDialog({ plan, onClose }: Props) {
  const notify = useNotify();
  const rename = useRenamePlan();
  const { control, handleSubmit, reset, setError } = useForm<{ name: string }>({
    resolver: zodResolver(planRenameSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (plan) reset({ name: plan.name });
  }, [plan, reset]);

  const submit = handleSubmit(async ({ name }) => {
    if (!plan) return;
    try {
      const updated = await rename.mutateAsync({ id: plan.id, name });
      notify.success(`Plan renamed to "${updated.name}".`);
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={Boolean(plan)}
      title="Edit Plan Name"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={rename.isPending}
      maxWidth="xs"
    >
      <Box sx={{ pt: 1 }}>
        <TextInput
          control={control}
          name="name"
          label="Plan Name"
          required
          autoFocus
          helperText={plan ? `Plan code ${plan.code}` : undefined}
        />
      </Box>
    </FormDialog>
  );
}
